import Image from 'next/future/image';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import './TeamsDiscoveryDrawer.scss';

import { Drawer } from '@wetransfer/transfer/component/Drawer/Drawer';
import { PLUS_ACCOUNT } from '@wetransfer/transfer/constant/subscriptions';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import Helper from '@wetransfer/transfer/helper';
import { userHasPro2022Tier } from '@wetransfer/transfer/helper/user';
import Analytics from '@wetransfer/transfer/modules/analytics';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import I18n from '@wetransfer/transfer/modules/i18n';
import { Button } from '@wetransfer/transfer/oneui/react';
import { IconCheck } from '@wetransfer/transfer/spaces-bundle/components/Icons/Icons';
import { State } from '@wetransfer/transfer/store/types';

import bottomImage from './Assets/drawer-graphic__transfer--bottom.png';
import bottomImage2x from './Assets/drawer-graphic__transfer--bottom@2x.png';
import middleImage from './Assets/drawer-graphic__transfer--middle.png';
import middleImage2x from './Assets/drawer-graphic__transfer--middle@2x.png';
import topImage from './Assets/drawer-graphic__transfer--top.png';
import topImage2x from './Assets/drawer-graphic__transfer--top@2x.png';
import shapeBackground from './Assets/shapeBackground.svg';
import {
  checkCookieData,
  checkDrawerCookie,
} from './TeamsDiscoveryDrawerHelper';

export interface Props {
  title: string;
  textContent: string;
  children?: JSX.Element[] | JSX.Element;
  setIsDrawerOpen?: (value: boolean) => void;
  setIsDrawerOpenForAnimation?: (value: boolean) => void;
}

export const TeamsDiscoveryDrawer = ({
  title,
  textContent,
  setIsDrawerOpen,
  setIsDrawerOpenForAnimation,
}: Props): JSX.Element => {
  const { user } = useSelector((state: State) => ({
    user: state.user,
  }));

  const [isDrawerOpen, setToOpen] = useState(true);

  const teamsDrawerDismissedForever = useMemo(
    () => Cookie.get(AppConfig.cookies.dismissTeamsDrawerForever.name),
    []
  );

  const teamsDrawerBeenSeenOnce = useMemo(
    () => Cookie.get(AppConfig.cookies.teamsDrawerSeenOnce.name),
    []
  );

  const drawerTimeout = 800;

  function addSnowplowTracking(action: string, label?: string) {
    const tracking = {
      category: 'transfer',
      action: action,
    };

    return Analytics.trackSnowplowEvent({
      ...tracking,
      ...(label && { label }),
    });
  }

  useEffect(() => {
    addSnowplowTracking('show_drawer', 'teams');
  }, []);

  const assetBackground = useMemo(
    () => (
      <div className="TeamsDiscovery__image-wrapper">
        <ul className="TeamsDiscovery__image-list">
          <li>
            <Image
              src={topImage}
              alt="transferTile1"
              srcSet={`${topImage2x} 2x`}
            />
          </li>
          <li>
            <Image
              src={middleImage}
              alt="transferTile2"
              srcSet={`${middleImage2x} 2x`}
            />
          </li>
          <li>
            <Image
              src={bottomImage}
              alt="transferTile3"
              srcSet={`${bottomImage2x} 2x`}
            />
          </li>
        </ul>
        <img
          src={shapeBackground}
          alt="Background shape"
          className="TeamsDiscovery__background-shape"
        />
      </div>
    ),
    []
  );

  const listItemText = useMemo(() => {
    const isPro2022 = userHasPro2022Tier(user);

    return [
      I18n.t('drawer.second_viewing.list.overview_item'),
      ...(isPro2022 && I18n.locale === 'en'
        ? [
            I18n.t('drawer.second_viewing.list.storage_limit_item', {
              storage_limit: PLUS_ACCOUNT.STORAGE_LIMIT,
            }),
          ]
        : []),
      I18n.t('drawer.second_viewing.list.branding_item'),
      I18n.t('drawer.second_viewing.list.subscription_item'),
    ];
  }, [user]);

  const firstDismissButton = (
    <Button
      small={true}
      square={true}
      variant="tertiary"
      onClick={dismissDrawer}
      label={I18n.t('drawer.first_viewing.dismiss_button')}
    />
  );

  const secondDismissButton = (
    <Button
      small={true}
      square={true}
      variant="tertiary"
      onClick={() => dismissDrawerForever(false)}
      label={I18n.t('drawer.second_viewing.dismiss_button')}
    />
  );

  const drawerContent = useMemo(
    () => (
      <ul className="TeamsDiscovery__benefit-list">
        {listItemText.map((item, index) => (
          <li key={index}>
            <IconCheck aria-hidden="true" />
            <p>{item}</p>
          </li>
        ))}
      </ul>
    ),
    [listItemText]
  );

  useEffect(() => {
    if (checkDrawerCookie()) {
      setIsDrawerOpen(false);
    }
  }, []);

  function goToTeamsUrl(): void {
    addSnowplowTracking('click_cta_invite_team');

    Analytics.trackGoogleEvent({
      category: 'transfer',
      action: 'click_cta_invite_team',
    });

    dismissDrawerForever(true);
  }

  function displayCorrectDismissButton():
    | JSX.Element[]
    | JSX.Element
    | boolean {
    const cookieData = checkCookieData();

    const numberOfDaysSinceLastSeen = cookieData.numberOfDaysSinceLastSeen;

    if (teamsDrawerDismissedForever) {
      return false;
    }

    if (!teamsDrawerBeenSeenOnce) {
      return firstDismissButton;
    } else if (teamsDrawerBeenSeenOnce && numberOfDaysSinceLastSeen > 3) {
      return secondDismissButton;
    }
  }

  function dismissDrawer(): void {
    setIsDrawerOpenForAnimation(true);
    setToOpen(false);
    setTimeout(() => {
      setIsDrawerOpen(false);
      const now = new Date();

      const formattedDate = `${
        now.getMonth() + 1
      }/${now.getDate()}/${now.getFullYear()}`;

      Cookie.set(AppConfig.cookies.teamsDrawerSeenOnce.name, formattedDate);

      addSnowplowTracking('click_cta_dismiss', 'not_today');
      setIsDrawerOpenForAnimation(false);
    }, drawerTimeout);
  }

  function dismissDrawerForever(redirection = false): void {
    setIsDrawerOpenForAnimation(true);
    setToOpen(false);

    setTimeout(() => {
      setIsDrawerOpen(false);
      Cookie.set(AppConfig.cookies.dismissTeamsDrawerForever.name, true);
      setIsDrawerOpenForAnimation(false);

      if (redirection) {
        const manageTeamUrl = Helper.getManageTeamUrl();
        window.location.href = manageTeamUrl;
      } else {
        addSnowplowTracking('click_cta_dismiss', 'dismiss_forever');
      }
    }, drawerTimeout);
  }

  return (
    <Drawer
      title={title}
      textContent={textContent}
      assets={assetBackground}
      content={drawerContent}
      isOpen={isDrawerOpen}
      timeout={drawerTimeout}
    >
      <>
        <Button
          className="TeamsDiscovery__main-cta"
          square={true}
          small={true}
          variant="primary"
          onClick={goToTeamsUrl}
          label={I18n.t('drawer.second_viewing.cta')}
        />
        {displayCorrectDismissButton()}
      </>
    </Drawer>
  );
};

export default TeamsDiscoveryDrawer;
